<template>
	<div class="toast" :class="toast.type" role="status">
		<span class="icon">
			<AppIcon :name="icon" :size="16" />
		</span>
		<span class="message">{{ t(toast.messageKey, toast.params ?? {}) }}</span>
		<button v-if="toast.action" type="button" class="action" @click="handleActionClick">
			{{ t(toast.action.labelKey) }}
		</button>
		<button type="button" class="close" :aria-label="t('callToActions.close')" @click="emit('dismiss', toast.id)">
			<AppIcon :name="IconName.CloseSmall" :size="12" />
		</button>
	</div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import { type Toast, ToastType } from "@/stores/toastStore"

const props = defineProps<{ toast: Toast }>()
const emit = defineEmits<{ dismiss: [id: number] }>()

const { t } = useI18n()

const icon = computed(
	() => props.toast.icon ?? (props.toast.type === ToastType.Success ? IconName.Check : IconName.CircleAlert)
)

let timer: ReturnType<typeof setTimeout> | undefined

onMounted(() => {
	if (props.toast.duration > 0) {
		timer = setTimeout(() => emit("dismiss", props.toast.id), props.toast.duration)
	}
})

function handleActionClick(): void {
	props.toast.action?.onClick()
	emit("dismiss", props.toast.id)
}

onBeforeUnmount(() => {
	if (timer) clearTimeout(timer)
})
</script>

<style scoped>
.toast {
	display: flex;
	align-items: center;
	gap: var(--spacing-sm);
	min-width: 240px;
	max-width: 360px;
	padding: var(--spacing-sm) var(--spacing-smd);
	background-color: var(--neutral-0);
	color: var(--neutral-900);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-base);
	box-shadow: var(--box-shadow-popup);
	font-family: var(--font-family-primary);
	font-size: var(--font-size-small);
	line-height: var(--line-height-normal);

	&.success {
		--accent: var(--primary-green);
	}

	&.error {
		--accent: var(--accent-warning);
	}

	.icon {
		display: flex;
		flex-shrink: 0;
		padding: var(--spacing-xs);
		border-radius: var(--border-radius-sm);
		color: var(--accent);
		background-color: color-mix(in srgb, var(--accent) 14%, transparent);
	}

	.message {
		flex: 1;
	}

	.action {
		flex-shrink: 0;
		padding: 0;
		background: none;
		border: none;
		color: var(--accent);
		font-weight: var(--font-weight-medium);
		cursor: pointer;

		&:hover {
			text-decoration: underline;
		}
	}

	.close {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0;
		background: none;
		border: none;
		color: var(--neutral-600);
		cursor: pointer;

		&:hover {
			color: var(--neutral-900);
		}
	}
}
</style>
